import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  CalendarDays,
  FileText,
  LogOut,
  User,
  Calendar,
  CheckCircle,
  CalendarOff,
  ChevronDown,
  UserCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAuth } from "../auth/AuthContext";
import { fetchTherapistProfile } from "../../lib/therapistApi";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Calendar", icon: CalendarDays },
  // { path: "/bookings", label: "Bookings", icon: Calendar },
  { path: "/check-ins", label: "Check-Ins", icon: CheckCircle },
  { path: "/availability", label: "Availability", icon: CalendarOff },
  { path: "/soap-notes", label: "SOAP Notes", icon: FileText },
  { path: "/reports", label: "Insights", icon: BarChart3 },
  { path: "/profile", label: "Profile", icon: User },
];

function Navigation({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [profile, setProfile] = useState(null);

  const goto = (path) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard" || location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  const handleLogoutClick = () => {
    logout();
    navigate("/login");
  };

  // Fetch profile data for display
  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.therapist_id) {
        try {
          const data = await fetchTherapistProfile(user.therapist_id);
          const therapistProfile = data?.therapist || data;
          setProfile(therapistProfile);
        } catch (error) {
          console.warn("Failed to fetch profile for navigation:", error);
        }
      }
    };

    fetchProfile();
  }, [user?.therapist_id]);

  // Get user display information
  const displayName =
    profile?.fullName ||
    profile?.full_name ||
    profile?.name ||
    user?.email?.split("@")[0] ||
    "Therapist";
  const userEmail = user?.email || "";
  const clinicId = user?.clinic_id || "";
  const therapistId = user?.therapist_id || "";

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return "T";
    const cleanName = name.replace("@", "").replace(".", "");
    return cleanName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <img
                  src="/logo-bat.png"
                  alt="BookaTherapy"
                  className="h-10 w-auto"
                />
                <div>
                  <span className="text-sm text-slate-500 ml-2">
                    Practitioner
                  </span>
                </div>
              </div>
              <nav className="ml-8 hidden items-center gap-1 lg:flex">
                {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
                  <Button
                    key={path}
                    variant={isActive(path) ? "default" : "ghost"}
                    size="sm"
                    onClick={() => goto(path)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      isActive(path)
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    }`}>
                    <Icon className="h-4 w-4" />
                    {label}
                  </Button>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              {/* Therapist Badge with Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-3 p-2 hover:bg-slate-100 rounded-lg">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <Avatar className="h-8 w-8">
                        {profile?.profilePhotoUrl && (
                          <AvatarImage src={profile.profilePhotoUrl} alt={displayName} />
                        )}
                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-semibold">
                          {getInitials(displayName)}
                        </AvatarFallback>
                      </Avatar>

                      {/* User Info */}
                      <div className="hidden md:flex flex-col items-start leading-tight">
                        <span className="text-sm font-medium text-slate-900">
                          {displayName}
                        </span>
                        <span className="text-xs text-slate-500">
                          {userEmail}
                        </span>
                      </div>

                      {/* Dropdown Arrow */}
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-64">
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {profile?.profilePhotoUrl && (
                          <AvatarImage src={profile.profilePhotoUrl} alt={displayName} />
                        )}
                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold">
                          {getInitials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {displayName}
                        </span>
                        <span className="text-xs text-slate-500 truncate">
                          {userEmail}
                        </span>
                      </div>
                    </div>
                    {clinicId && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500">
                          Clinic: {clinicId}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Menu Items */}
                  <DropdownMenuItem
                    onClick={handleProfileClick}
                    className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-slate-50">
                    <UserCircle className="h-4 w-4 text-slate-600" />
                    <span className="text-sm">Profile</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={handleLogoutClick}
                    className="flex items-center gap-3 px-4 py-2 cursor-pointer text-red-600 hover:bg-red-50 focus:text-red-600 focus:bg-red-50">
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <nav className="flex gap-2 pb-4 lg:hidden overflow-x-auto">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <Button
                key={path}
                variant={isActive(path) ? "default" : "outline"}
                size="sm"
                onClick={() => goto(path)}
                className="flex-shrink-0 gap-2 whitespace-nowrap">
                <Icon className="h-4 w-4" />
                {label}
              </Button>
            ))}
          </nav>
        </div>
      </header>
      <main className="bg-slate-50">{children}</main>
    </div>
  );
}

export default Navigation;
